import React, { useEffect, useId } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'motion/react';
import { duration } from './primitives/tokens';
import { Portal } from './primitives/Portal';
import { FocusTrap } from './primitives/FocusTrap';
import { IconButton } from './Button';
import { Icon } from './Icon';
import { zIndex as Z } from '../../tokens/zIndex';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { drawerSlide } from './primitives/motion';
import styles from './Drawer.module.css';

const drawerPanel = cva(styles.panel, {
  variants: {
    placement: {
      left: styles.left,
      right: styles.right,
      top: styles.top,
      bottom: styles.bottom,
    },
    floating: { true: styles.floating },
  },
  defaultVariants: { placement: 'right' },
});

type DrawerPanelVariants = VariantProps<typeof drawerPanel>;

type DrawerPlacement = 'left' | 'right' | 'top' | 'bottom';

interface DrawerProps extends DrawerPanelVariants {
  open: boolean;
  onClose: () => void;
  placement?: DrawerPlacement;
  width?: number | string;
  height?: number | string;
  title?: React.ReactNode;
  footer?: React.ReactNode;
  mask?: boolean;
  maskClosable?: boolean;
  destroyOnClose?: boolean;
  extra?: React.ReactNode;
  zIndex?: number;
  children?: React.ReactNode;
  bodyStyle?: React.CSSProperties;
  /** When false, disables body scroll lock and focus trap so content behind remains interactive */
  blocking?: boolean;
  /** When true, renders as a floating panel inset from the edge with full border-radius */
  floating?: boolean;
  /** Close button position — defaults to 'left' */
  closePosition?: 'left' | 'right';
}


/**
 * Drawer — sliding panel from any edge.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | open | boolean | — | Visibility |
 * | onClose | () => void | — | Close handler |
 * | placement | 'left' \| 'right' \| 'top' \| 'bottom' | 'right' | Slide direction |
 * | width | number \| string | 378 | Width (left/right) |
 * | height | number \| string | 378 | Height (top/bottom) |
 * | title | ReactNode | — | Header title |
 * | footer | ReactNode | — | Footer content |
 * | mask | boolean | true | Show overlay |
 * | maskClosable | boolean | true | Close on mask click |
 * | destroyOnClose | boolean | false | Unmount children on close |
 * | extra | ReactNode | — | Header right slot |
 * | zIndex | number | Z.modal | z-index |
 * | bodyStyle | CSSProperties | — | Body styles |
 *
 * @example
 * <Drawer open={open} onClose={close} title="Details">
 *   <p>Drawer content</p>
 * </Drawer>
 */
export function Drawer({
  open,
  onClose,
  placement = 'right',
  width = 378,
  height = 378,
  title,
  footer,
  mask = true,
  maskClosable = true,
  destroyOnClose: _destroyOnClose = false,
  extra,
  zIndex = Z.modal,
  children,
  bodyStyle,
  blocking = true,
  floating = false,
  closePosition = 'left',
}: DrawerProps) {
  const titleId = useId();
  const shouldReduce = useReducedMotion();

  // Lock body scroll (only in blocking mode)
  useEffect(() => {
    if (!open || !blocking) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, blocking]);

  // Escape key
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const isHorizontal = placement === 'left' || placement === 'right';
  const resolvedSize = isHorizontal
    ? (typeof width === 'number' ? `${width}px` : width)
    : (typeof height === 'number' ? `${height}px` : height);

  // When reduced motion is requested, use opacity-only transition
  const slideInitial = shouldReduce
    ? { opacity: 0 }
    : isHorizontal
      ? { x: placement === 'right' ? '100%' : '-100%' }
      : { y: placement === 'bottom' ? '100%' : '-100%' };
  const slideAnimate = shouldReduce ? { opacity: 1 } : { x: 0, y: 0, opacity: 1 };
  const slideExit = shouldReduce
    ? { opacity: 0 }
    : isHorizontal
      ? { x: placement === 'right' ? '100%' : '-100%' }
      : { y: placement === 'bottom' ? '100%' : '-100%' };

  return (
    <AnimatePresence>
      {open && (
        <Portal>
          {/* Mask */}
          {mask && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: duration.emphasis / 1000 }}
              className={styles.mask}
              style={{ '--drawer-z': zIndex } as React.CSSProperties}
              onClick={maskClosable ? onClose : undefined}
            />
          )}

          {/* Panel */}
          <FocusTrap active={blocking && open} onEscape={onClose} aria-labelledby={title ? titleId : undefined}>
            <motion.div
              data-floating={floating ? 'true' : undefined}
              initial={slideInitial}
              animate={slideAnimate}
              exit={slideExit}
              transition={shouldReduce ? { duration: 0 } : drawerSlide.transition}
              className={clsx(drawerPanel({ placement, floating: floating || undefined }))}
              style={{ '--drawer-panel-z': zIndex + 1, '--drawer-size': resolvedSize } as React.CSSProperties}
            >
              {/* Header */}
              {(title || extra) && (
                <div className={styles.header}>
                  {closePosition === 'left' && (
                    <div className={styles.headerLeft}>
                      <IconButton
                        size="sm"
                        pill
                        hoverBackground="var(--surface-container-high)"
                        onClick={onClose}
                        aria-label="Close"
                        title="Close"
                      >
                        <Icon name="close" size={16} />
                      </IconButton>
                      {title && (
                        <div id={titleId} className={styles.headerTitle}>
                          {title}
                        </div>
                      )}
                    </div>
                  )}
                  {closePosition === 'right' && title && (
                    <div id={titleId} className={styles.headerTitle}>
                      {title}
                    </div>
                  )}
                  <div className={styles.headerExtra}>
                    {extra}
                    {closePosition === 'right' && (
                      <IconButton
                        size="sm"
                        pill
                        hoverBackground="var(--surface-container-high)"
                        onClick={onClose}
                        aria-label="Close"
                        title="Close"
                      >
                        <Icon name="close" size={16} />
                      </IconButton>
                    )}
                  </div>
                </div>
              )}

              {/* Body */}
              <div className={styles.body} style={bodyStyle}>
                {children}
              </div>

              {/* Footer */}
              {footer && (
                <div className={styles.drawerFooter}>
                  {footer}
                </div>
              )}
            </motion.div>
          </FocusTrap>
        </Portal>
      )}
    </AnimatePresence>
  );
}
